import styled from '@emotion/styled';
import { useStudent } from '../../context/StudentContext';
import { getCourseProgress } from '../../utils';
import { screenSizes } from 'src/assets/styles/Literals';

const WelcomeScreenWrapper = styled.div`
  .welcome-screen {
    text-align: center;
    padding: 3rem 2rem;
    max-width: 800px;
    margin: 0 auto;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 2rem 1rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 1.5rem 0.5rem;
    }
  }

  .welcome-title {
    font-family: 'Raleway', sans-serif;
    font-size: 2.5rem;
    color: #2c3e50;
    margin-bottom: 1.5rem;

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 2rem;
      margin-bottom: 1rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 1.75rem;
      margin-bottom: 0.75rem;
    }
  }

  .welcome-description {
    font-size: 1.1rem;
    color: #666;
    line-height: 1.6;
    margin-bottom: 2rem;

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 0.95rem;
      margin-bottom: 1rem;
    }
  }

  .course-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;

    @media (max-width: ${screenSizes.tablet}) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
      margin: 1.5rem 0;
    }

    @media (max-width: ${screenSizes.mobile}) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
      margin: 1rem 0;
    }
  }

  .stat-card {
    background-color: #f8f9fa;
    padding: 1.5rem;
    border-radius: 8px;
    text-align: center;
    transition: transform 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: ${screenSizes.tablet}) {
      padding: 1.25rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 1rem;
    }
  }

  .stat-value {
    font-size: 2rem;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 0.5rem;

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 1.75rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 1.5rem;
    }
  }

  .stat-label {
    color: #666;
    font-size: 0.9rem;

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 0.85rem;
    }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Raleway', sans-serif;
    font-weight: 500;
    min-width: 200px;

    &:hover {
      background-color: #45a049;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
    }

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.9rem 1.8rem;
      font-size: 1rem;
      min-width: 180px;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.8rem 1.5rem;
      font-size: 0.95rem;
      min-width: 160px;
    }
  }
`;

export const WelcomeScreenComponent = (props) => {
  const { course, courseProgress, stats, setCurrentLesson } = props;
  const { student } = useStudent();
  const currentProgress = getCourseProgress(student, course.id);

  const findNextLesson = () => {
    if (!course?.AcademyLessons) return null;

    const nextLesson = course.AcademyLessons.find(
      (l) => !currentProgress?.completedLessons?.includes(l.id) && !l.locked
    );
    return nextLesson;
  };

  const handleContinue = () => {
    if (courseProgress > 0) {
      const nextLesson = findNextLesson();
      if (nextLesson) {
        setCurrentLesson(nextLesson);
      }
    } else {
      const firstUnlockedLesson = course.AcademyLessons.find((lesson) => !lesson.locked);
      if (firstUnlockedLesson) {
        setCurrentLesson(firstUnlockedLesson);
      }
    }
  };

  return (
    <WelcomeScreenWrapper>
      <div className="welcome-screen">
        <h1 className="welcome-title">Bienvenido a {course.title}</h1>
        <p className="welcome-description">{course.description}</p>

        <div className="course-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalCategories}</div>
            <div className="stat-label">Módulos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalLessons}</div>
            <div className="stat-label">Lecciones</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{courseProgress}%</div>
            <div className="stat-label">Progreso</div>
          </div>
        </div>

        <button className="start-button" onClick={handleContinue}>
          {courseProgress > 0 ? 'Continuar Curso' : 'Comenzar Curso'}
        </button>
      </div>
    </WelcomeScreenWrapper>
  );
};

export { WelcomeScreenComponent as WelcomeScreen };
